import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import EditIcon from '@mui/icons-material/Edit'

import { openWisDetailsModal } from 'app/entities/store/modalsSlice'
import { useAppDispatch } from 'app/hooks'
import { useUpdateQRASavedStateMutation } from 'app/services/api/qra/savedstates'
import { alphanumericRegex } from 'constants/regex'
import { ISavedState } from 'features/RiskManagement/Assessments'
import { setSavedStateDetailsIdSync } from 'features/RiskManagement/Assessments/States/qraSavedStateSlice'
import EditableInput from 'features/RiskManagement/Components/EditableInput'

import { formatDate } from './formatDate'

interface ISavedStateCard {
  state: ISavedState
}

const SavedStateCard = ({ state }: ISavedStateCard) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { search } = useLocation()
  const [updateSavedState] = useUpdateQRASavedStateMutation()
  const [isEditName, setIsEditName] = useState<boolean>(false)

  if (state == null) {
    return <div>State Data not found</div>
  }

  const handleSaveInput = (newName: string): void => {
    const requestBody: Partial<ISavedState> = {
      id: state.id,
      name: newName
    }

    updateSavedState(requestBody).finally(() => {
      setIsEditName(false)
    })
  }

  const openDetails = () => {
    dispatch(setSavedStateDetailsIdSync(state.id))

    dispatch(openWisDetailsModal())
  }

  const openSavedState = () => {
    dispatch(setSavedStateDetailsIdSync(state.id))

    navigate(`/risk-management/assessments/states/${state.id}${search}`)
  }

  return (
    <div className='flex flex-col max-w-2xl rounded-sm bg-white h-64'>
      <div
        className={[
          'flex items-center justify-between',
          'px-6 py-3',
          'border-b border-gray-200'
        ].join(' ')}
      >
        <div className='flex flex-1 items-center font-bold'>
          {isEditName ? (
            <EditableInput
              setEditInProgress={setIsEditName}
              handleSaveInput={handleSaveInput}
              validationPattern={alphanumericRegex}
              invalidInputMessage='State name can only contain alphanumeric characters and underscore'
            />
          ) : (
            <div className='flex flex-1 justify-between items-center'>
              <div>
                {state.name}
                <button onClick={() => setIsEditName(true)}>
                  <EditIcon className='pl-2 text-gray-700' />
                </button>
              </div>
              <div>
                <button
                  className='w-16 font-semibold text-gray-700'
                  onClick={openDetails}
                >
                  DETAILS
                </button>
                <button
                  className='w-16 font-semibold text-lighthouseBlue'
                  onClick={openSavedState}
                >
                  OPEN
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div
        className={[
          'h-full flex flex-col justify-between',
          'px-6 pb-3'
        ].join(' ')}
      >
        <div className='grid grid-flow-row grid-cols-1'>
          <span className='py-3 font-bold text-sm text-gray-500'>
            WHAT-IF SCENARIOS
          </span>
          <div className='grid grid-flow-row'>
            <ul>
              {state.assessments?.slice(0, 5).map((scenario) => {
                return (
                  <li key={scenario.id} className='grid grid-cols-2'>
                    <div>
                      <span className='text-sm' title={scenario.name}>
                        {scenario.name.substring(0, 31) +
                          (scenario.name.length > 30 ? '...' : '') +
                          (scenario.isPublished ? '' : '*')}
                      </span>
                    </div>
                    <div className='grid justify-items-end'>
                      <span className='pr-2 text-gray-500 text-sm'>
                        {formatDate(scenario.dateCreated, 'short')}
                      </span>
                    </div>
                  </li>
                )
              })}
              {state.assessments.length > 5 && (
                <div className='text-sm text-gray-500'>{`+${state.assessments.length - 5} more scenario(s)`}</div>
              )}
            </ul>
          </div>
        </div>
        <div className='text-gray-500 text-[11px]'>
          {`Saved ${formatDate(state.dateCreated, 'medium')} by ${
            state.author
          }`}
        </div>
      </div>
    </div>
  )
}

export default SavedStateCard
